import type { BoggleSpecialTileKind } from '~/boggle/engine/specialTiles'
import {
  BOGGLE_GOLD_TILE_KIND,
  buildEffectiveSpecialTileCounts,
  buildPhysicalSpecialTileCounts,
  createEmptySpecialTileCounts,
  getEffectiveGoldCount,
  getVirtualGoldCount,
  type BoggleLegacyAcceptedWordPathStep,
} from '~/boggle/engine/specialTiles'
import type {
  BoggleCompletedRunAcceptedWordFact,
  BoggleCompletedRunAcceptedWordPathStepFact,
  BoggleCompletedRunSummarySnapshot,
} from './types'

export type BoggleCompletedRunAcceptedWordPathStepFactInput = {
  letter: string
  position: BoggleCompletedRunAcceptedWordPathStepFact['position']
  effectiveGoldSource?: BoggleCompletedRunAcceptedWordPathStepFact['effectiveGoldSource']
  effectiveSpecialKinds?: readonly BoggleSpecialTileKind[]
  physicalSpecialKinds?: readonly BoggleSpecialTileKind[]
  specialKinds?: readonly BoggleSpecialTileKind[]
  virtualSpecialKinds?: readonly BoggleSpecialTileKind[]
}

export function normalizeCompletedRunPathStep(
  step: BoggleCompletedRunAcceptedWordPathStepFactInput,
): BoggleCompletedRunAcceptedWordPathStepFact {
  if (step.physicalSpecialKinds !== undefined && step.virtualSpecialKinds !== undefined) {
    return {
      effectiveSpecialKinds:
        step.effectiveSpecialKinds ??
        [...new Set([...step.physicalSpecialKinds, ...step.virtualSpecialKinds])],
      letter: step.letter,
      physicalSpecialKinds: step.physicalSpecialKinds,
      position: { ...step.position },
      virtualSpecialKinds: step.virtualSpecialKinds,
    }
  }

  const physicalSpecialKinds: readonly BoggleSpecialTileKind[] = [
    ...(step.specialKinds ?? step.physicalSpecialKinds ?? []),
  ]
  const virtualSpecialKinds: readonly BoggleSpecialTileKind[] =
    step.virtualSpecialKinds !== undefined
      ? [...step.virtualSpecialKinds]
      : step.effectiveGoldSource === 'gold-rush' &&
          !physicalSpecialKinds.includes(BOGGLE_GOLD_TILE_KIND)
        ? [BOGGLE_GOLD_TILE_KIND]
        : []

  const effectiveSpecialKinds: readonly BoggleSpecialTileKind[] =
    step.effectiveSpecialKinds !== undefined
      ? [...step.effectiveSpecialKinds]
      : [...new Set([...physicalSpecialKinds, ...virtualSpecialKinds])]

  return {
    effectiveSpecialKinds,
    letter: step.letter,
    physicalSpecialKinds,
    position: { ...step.position },
    virtualSpecialKinds,
  }
}

export function getAcceptedWordEffectiveSpecialTileCounts(
  word: BoggleCompletedRunAcceptedWordFact,
): Readonly<Record<BoggleSpecialTileKind, number>> {
  if (word.effectiveSpecialTileCountsUsed) {
    return word.effectiveSpecialTileCountsUsed
  }
  if (word.pathSteps && word.pathSteps.length > 0) {
    return buildEffectiveSpecialTileCounts(word.pathSteps)
  }
  const counts = createEmptySpecialTileCounts()
  counts[BOGGLE_GOLD_TILE_KIND] = word.effectiveGoldTilesUsed ?? 0
  if (word.specialTileCountsUsed) {
    for (const [kind, count] of Object.entries(word.specialTileCountsUsed)) {
      counts[kind as BoggleSpecialTileKind] = count
    }
  }
  return counts
}

export function getAcceptedWordPhysicalSpecialTileCounts(
  word: BoggleCompletedRunAcceptedWordFact,
): Readonly<Record<BoggleSpecialTileKind, number>> {
  if (word.physicalSpecialTileCountsUsed) {
    return word.physicalSpecialTileCountsUsed
  }
  if (word.pathSteps && word.pathSteps.length > 0) {
    return buildPhysicalSpecialTileCounts(word.pathSteps)
  }
  if (word.specialTileCountsUsed) {
    return { ...word.specialTileCountsUsed }
  }
  const counts = createEmptySpecialTileCounts()
  counts[BOGGLE_GOLD_TILE_KIND] = word.physicalGoldTilesUsed ?? 0
  return counts
}

export function getAcceptedWordEffectiveGoldCount(word: BoggleCompletedRunAcceptedWordFact): number {
  return getEffectiveGoldCount(getAcceptedWordEffectiveSpecialTileCounts(word))
}

export function getAcceptedWordPhysicalGoldCount(word: BoggleCompletedRunAcceptedWordFact): number {
  return getEffectiveGoldCount(getAcceptedWordPhysicalSpecialTileCounts(word))
}

export function getAcceptedWordVirtualGoldCount(word: BoggleCompletedRunAcceptedWordFact): number {
  if (word.virtualGoldTilesUsed !== undefined) {
    return word.virtualGoldTilesUsed
  }
  return getVirtualGoldCount(
    getAcceptedWordEffectiveSpecialTileCounts(word),
    getAcceptedWordPhysicalSpecialTileCounts(word),
  )
}

export function sumAcceptedWordEffectiveSpecialTileCounts(
  acceptedWordTimeline: readonly BoggleCompletedRunAcceptedWordFact[],
): Readonly<Record<BoggleSpecialTileKind, number>> {
  const totals = createEmptySpecialTileCounts()
  for (const word of acceptedWordTimeline) {
    const counts = getAcceptedWordEffectiveSpecialTileCounts(word)
    for (const kind of Object.keys(totals) as BoggleSpecialTileKind[]) {
      totals[kind] += counts[kind]
    }
  }
  return totals
}

export function sumAcceptedWordPhysicalSpecialTileCounts(
  acceptedWordTimeline: readonly BoggleCompletedRunAcceptedWordFact[],
): Readonly<Record<BoggleSpecialTileKind, number>> {
  const totals = createEmptySpecialTileCounts()
  for (const word of acceptedWordTimeline) {
    const counts = getAcceptedWordPhysicalSpecialTileCounts(word)
    for (const kind of Object.keys(totals) as BoggleSpecialTileKind[]) {
      totals[kind] += counts[kind]
    }
  }
  return totals
}

export function getSummaryEffectiveGoldTilesPlayed(
  snapshot: BoggleCompletedRunSummarySnapshot | undefined,
): number {
  if (snapshot?.effectiveSpecialTileCounts) {
    return snapshot.effectiveSpecialTileCounts[BOGGLE_GOLD_TILE_KIND] ?? 0
  }
  return snapshot?.goldTilesPlayed ?? 0
}

export type LegacyAcceptedWordPathStepInput = BoggleLegacyAcceptedWordPathStep
